import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { sendWebhook } from '../utils/webhook';
import type { FormData } from '../types';
import SubmissionPopup from '../components/SubmissionPopup';

const blogPosts = [
  {
    id: 1,
    title: "The Art of Visual Storytelling in Content Creation",
    excerpt: "Discover how visual elements can transform your brand narrative and create deeper connections with your audience.",
    content: `Visual storytelling is more than just adding images to your content—it's about creating a narrative that resonates with your audience on an emotional level. In this comprehensive guide, we explore the key elements of effective visual storytelling and how it can transform your brand's message.

Key Elements of Visual Storytelling:

1. Color Psychology in Brand Storytelling
• Understanding emotional responses to different colors
• Creating cohesive color palettes that reflect brand values
• Using color contrast to guide viewer attention
• Implementing seasonal color strategies for campaigns

2. Composition Techniques
• Rule of thirds and visual hierarchy
• Leading lines and directional cues
• Balance and symmetry in design
• Negative space utilization
• Dynamic vs. static compositions

3. Brand Consistency
• Developing a consistent visual language
• Creating reusable design elements and templates
• Maintaining brand identity across platforms
• Adapting visuals for different channels while preserving brand essence

4. Emotional Resonance
• Creating authentic connections through imagery
• Storytelling through human elements
• Using metaphor and symbolism effectively
• Building narrative arcs in visual content

5. Technical Considerations
• Image resolution and quality standards
• Platform-specific optimization
• Mobile-first visual design
• Accessibility in visual content

6. Implementation Strategies
• Content planning and visual asset management
• Creating visual content calendars
• Batch production techniques
• Quality control processes

7. Measuring Success
• Key metrics for visual content
• A/B testing visual elements
• Audience engagement analysis
• ROI tracking for visual campaigns

Best Practices for Visual Storytelling:

1. Start with Strategy
• Define clear objectives
• Identify target audience preferences
• Align with overall marketing goals
• Create measurable success metrics

2. Content Development
• Storyboard your concepts
• Create mood boards
• Develop style guides
• Plan content series and themes

3. Production Guidelines
• Establish quality standards
• Define technical specifications
• Create workflow processes
• Implement review procedures

4. Distribution Planning
• Platform-specific optimization
• Timing and scheduling
• Cross-platform promotion
• Engagement monitoring

Remember: Effective visual storytelling isn't just about creating beautiful images—it's about crafting a cohesive narrative that drives engagement and supports your business objectives.

Advanced Tips:

1. Visual Hierarchy
• Guide viewer attention
• Emphasize key messages
• Create clear call-to-actions
• Maintain consistent structure

2. Platform Optimization
• Instagram-specific strategies
• LinkedIn professional presentation
• Twitter visual engagement
• TikTok native content

3. Trend Integration
• Stay current while maintaining brand identity
• Adapt trends to fit your style
• Create timeless content
• Balance trending and evergreen content

4. Content Repurposing
• Transform long-form into visual content
• Create platform variations
• Maintain consistent messaging
• Optimize for each channel

Implementation Checklist:

□ Define brand visual guidelines
□ Create content templates
□ Establish quality control process
□ Set up performance tracking
□ Plan content calendar
□ Prepare asset library
□ Train team members
□ Monitor and adjust strategy

By following these guidelines and consistently implementing strong visual storytelling principles, you'll create content that not only captures attention but also drives meaningful engagement with your audience.`,
    image: "https://images.unsplash.com/photo-1542744094-24638eff58bb?auto=format&fit=crop&q=80",
    date: "March 1, 2024",
    readTime: "8 min read",
    category: "Content Creation"
  },
  {
    id: 2,
    title: "Maximizing Social Media Impact in 2024",
    excerpt: "Learn the latest strategies and trends that are shaping social media marketing this year.",
    content: `As we dive into 2024, social media continues to evolve at a rapid pace. This comprehensive guide explores the latest trends, strategies, and best practices for maximizing your social media impact.

Key Trends for 2024:

1. AI-Powered Content Optimization
• Machine learning for content scheduling
• Automated performance analysis
• Predictive engagement metrics
• Personalized content recommendations
• AI-assisted caption generation
• Smart hashtag optimization
• Engagement pattern analysis

2. Short-Form Video Dominance
• Platform-specific video strategies
• Vertical video optimization
• Story-first content approach
• Quick-cut editing techniques
• Sound-on vs. sound-off content
• Interactive video elements
• Cross-platform video adaptation

3. Community-Driven Marketing
• User-generated content campaigns
• Community management strategies
• Engagement-focused content
• Interactive features utilization
• Community guidelines development
• Ambassador programs
• Feedback loops implementation

4. Social Commerce Integration
• Shopping feature optimization
• Product tagging strategies
• Live shopping events
• Catalog management
• Payment integration
• Customer service via social
• Analytics tracking

Advanced Implementation Strategies:

1. Content Planning
• Content calendar development
• Theme-based campaigns
• Cross-platform coordination
• Asset management
• Quality control processes
• Team workflow optimization
• Performance tracking

2. Engagement Optimization
• Response time management
• Conversation starters
• Interactive content types
• Community guidelines
• Crisis management plans
• Engagement measurement
• Team training protocols

3. Analytics and Reporting
• KPI definition
• Tracking systems setup
• Regular reporting schedule
• Data analysis processes
• ROI measurement
• Adjustment protocols
• Team performance metrics

4. Platform-Specific Strategies
• Instagram Reels optimization
• TikTok content creation
• LinkedIn thought leadership
• Twitter engagement tactics
• Facebook group management
• YouTube strategy
• Pinterest marketing

Best Practices for Implementation:

1. Content Creation
• Brand voice guidelines
• Visual style guide
• Content templates
• Asset library
• Quality standards
• Review process
• Team training

2. Community Management
• Response protocols
• Engagement guidelines
• Crisis management
• Team structure
• Tools and software
• Performance metrics
• Training programs

3. Performance Tracking
• Analytics setup
• Regular reporting
• KPI monitoring
• ROI tracking
• Team metrics
• Client reporting
• Strategy adjustment

4. Team Management
• Roles and responsibilities
• Communication protocols
• Quality control
• Training programs
• Tool proficiency
• Performance reviews
• Growth planning

Implementation Checklist:

□ Strategy documentation
□ Team training completion
□ Tool setup and integration
□ Content calendar creation
□ Analytics configuration
□ Community guidelines
□ Crisis management plan
□ Regular review schedule

Success Metrics:

• Engagement rates
• Follower growth
• Conversion tracking
• Response times
• Brand sentiment
• ROI measurement
• Team performance

By implementing these strategies and staying current with platform changes, you'll be well-positioned to maximize your social media impact in 2024 and beyond.`,
    image: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&q=80",
    date: "February 28, 2024",
    readTime: "10 min read",
    category: "Social Media"
  },
  {
    id: 3,
    title: "Building a Strong Brand Identity Through Content",
    excerpt: "Explore how consistent, high-quality content can establish and reinforce your brand's unique identity.",
    content: `Your brand identity is more than just your logo and color scheme—it's the complete story of who you are and what you stand for. This comprehensive guide explores how strategic content creation can help build and strengthen your brand identity.

Key Components of Brand Identity:

1. Voice and Tone Guidelines
• Brand personality definition
• Communication style guide
• Language preferences
• Emotional resonance
• Audience adaptation
• Consistency rules
• Examples and templates

2. Visual Consistency
• Logo usage guidelines
• Color palette standards
• Typography rules
• Image style guide
• Design elements
• Layout principles
• Platform adaptations

3. Content Themes and Pillars
• Core message development
• Topic categories
• Content mix strategy
• Platform distribution
• Audience alignment
• Value proposition
• Brand storytelling

4. Brand Storytelling Framework
• Origin story
• Mission and values
• Customer journey
• Success stories
• Team spotlight
• Future vision
• Community impact

Implementation Strategies:

1. Content Development
• Editorial calendar
• Style guide creation
• Quality standards
• Review process
• Team training
• Asset management
• Performance tracking

2. Brand Guidelines
• Visual standards
• Voice guidelines
• Platform specifics
• Usage examples
• Do's and don'ts
• Team training
• Update process

3. Quality Control
• Review checklist
• Approval process
• Team roles
• Tools and software
• Performance metrics
• Feedback loops
• Improvement plans

4. Distribution Strategy
• Channel selection
• Content adaptation
• Timing optimization
• Audience targeting
• Engagement monitoring
• Performance tracking
• Strategy adjustment

Advanced Considerations:

1. Brand Evolution
• Growth planning
• Market adaptation
• Trend integration
• Identity protection
• Change management
• Team alignment
• Customer feedback

2. Crisis Management
• Response protocols
• Team preparation
• Communication plan
• Recovery strategy
• Prevention measures
• Documentation
• Review process

3. Measurement and Analysis
• KPI definition
• Tracking systems
• Regular reporting
• ROI calculation
• Team metrics
• Client feedback
• Strategy updates

4. Team Development
• Training programs
• Skill assessment
• Tool proficiency
• Communication
• Performance review
• Growth planning
• Knowledge sharing

Implementation Checklist:

□ Brand guidelines document
□ Content strategy plan
□ Team training schedule
□ Tool setup complete
□ Monitoring systems
□ Review process
□ Crisis plan
□ Update schedule

Success Metrics:

• Brand recognition
• Engagement rates
• Conversion tracking
• Customer feedback
• Team alignment
• ROI measurement
• Market position

By following these guidelines and maintaining consistency in your brand identity implementation, you'll create a strong, recognizable brand that resonates with your target audience and drives business success.`,
    image: "https://images.unsplash.com/photo-1493612276216-ee3925520721?auto=format&fit=crop&q=80",
    date: "February 25, 2024",
    readTime: "7 min read",
    category: "Branding"
  }
];

export default function Blog() {
  const [selectedPost, setSelectedPost] = useState<typeof blogPosts[0] | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);

    const formData: FormData = {
      email,
      type: 'newsletter',
      timestamp: new Date().toISOString(),
      source: 'blog_page'
    };

    try {
      await sendWebhook(formData);
      setEmail('');
      setShowPopup(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(blogPosts.length / postsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="page-container">
      <div className="content-container">
        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl font-bold mb-4"
          >
            Insights & <span className="text-golden">Stories</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-300"
          >
            Expert tips, industry trends, and creative inspiration
          </motion.p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {currentPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-card-gradient rounded-2xl overflow-hidden border border-gray-800 hover:border-golden transition-colors"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform hover:scale-105"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-golden text-white text-sm font-medium rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center text-sm text-gray-400 mb-4">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{post.date}</span>
                  <Clock className="w-4 h-4 ml-4 mr-2" />
                  <span>{post.readTime}</span>
                </div>
                <h2 className="text-xl font-bold mb-3 line-clamp-2 text-white">
                  {post.title}
                </h2>
                <p className="text-gray-300 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <button 
                  onClick={() => setSelectedPost(post)}
                  className="inline-flex items-center text-golden font-semibold hover:opacity-80 transition-opacity"
                >
                  Read More <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center items-center space-x-4 mb-12">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-full ${
              currentPage === 1
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-golden hover:bg-golden/10'
            }`}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`w-10 h-10 rounded-full ${
                currentPage === index + 1
                  ? 'bg-golden text-white'
                  : 'text-gray-300 hover:bg-golden/10'
              }`}
            >
              {index + 1}
            </button>
          ))}
          
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-full ${
              currentPage === totalPages
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-golden hover:bg-golden/10'
            }`}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Newsletter Section */}
        <div className="bg-card-gradient rounded-2xl p-8 md:p-12 mb-12 border border-gray-800">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-gray-300 mb-6">
              Subscribe to our newsletter for the latest content creation tips and industry insights.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-golden text-white rounded-full font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Blog Post Modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card-gradient rounded-2xl p-6 md:p-8 max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <span className="px-3 py-1 bg-golden text-white text-sm font-medium rounded-full">
                  {selectedPost.category}
                </span>
                <button
                  onClick={() => setSelectedPost(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <img 
                src={selectedPost.image} 
                alt={selectedPost.title}
                className="w-full h-64 object-cover rounded-xl mb-6"
              />
              <div className="flex items-center text-sm text-gray-400 mb-4">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{selectedPost.date}</span>
                <Clock className="w-4 h-4 ml-4 mr-2" />
                <span>{selectedPost.readTime}</span>
              </div>
              <h2 className="text-2xl font-bold mb-4">{selectedPost.title}</h2>
              <div className="text-gray-300 space-y-4 whitespace-pre-line">
                {selectedPost.content}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <SubmissionPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </div>
  );
}